import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, ChevronRight, Plus, Layers, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#F59E0B");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [renaming, setRenaming] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === "SUBAPPALTATORE") {
      navigate("/portale/ticket", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", { name, description, color });
      toast.success("Gruppo creato");
      setOpen(false); setName(""); setDescription("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-600 dark:text-amber-500 font-semibold">Prima pagina</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Gruppi cantieri</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Seleziona un gruppo per accedere ai relativi cantieri, ticket e calendario materiali.</p>
        </div>
        {user?.role === "ADMIN" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-group-btn">
                <Plus className="h-4 w-4 mr-2" /> Nuovo gruppo
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Nuovo gruppo cantieri</DialogTitle><DialogDescription>Compila i dati del nuovo gruppo di cantieri.</DialogDescription></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label htmlFor="gname">Nome</Label>
                  <Input id="gname" required value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name-input" />
                </div>
                <div>
                  <Label htmlFor="gdesc">Descrizione</Label>
                  <Textarea id="gdesc" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="group-description-input" />
                </div>
                <div>
                  <Label htmlFor="gcolor">Colore</Label>
                  <Input id="gcolor" type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="group-color-input" className="h-10 w-24" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="group-create-submit">Crea</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500">Caricamento…</div>
      ) : groups.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center">
            <Layers className="h-10 w-10 mx-auto text-slate-400 mb-3" />
            <p className="text-slate-600 dark:text-slate-300">Nessun gruppo disponibile.</p>
            {user?.role === "ADMIN" && <p className="text-sm text-slate-500 mt-1">Crea il primo gruppo per iniziare.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {groups.map((g) => (
            <div key={g.id} className="relative">
              <button
                onClick={() => navigate(`/g/${g.id}/cantieri`)}
                data-testid={`group-card-${g.id}`}
                className="group text-left block w-full"
              >
                <Card className="h-full transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-amber-500 dark:hover:border-amber-500">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: (g.color || "#F59E0B") + "22", color: g.color || "#F59E0B" }}>
                        <Building2 className="h-6 w-6" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="text-lg font-bold tracking-tight">{g.name}</h3>
                    {g.description && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{g.description}</p>}
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Cantieri</span>
                      <span className="font-bold text-lg text-slate-900 dark:text-slate-100">{g.cantieri_count}</span>
                    </div>
                  </CardContent>
                </Card>
              </button>
              {user?.role === "ADMIN" && (
                <>
                <button
                  onClick={(e) => { e.stopPropagation(); setRenameTarget(g); setRenameName(g.name); }}
                  data-testid={`group-rename-${g.id}`}
                  title="Rinomina gruppo"
                  className="absolute top-3 right-[5.25rem] p-1.5 rounded-md bg-white/80 dark:bg-slate-800/80 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 opacity-70 hover:opacity-100 transition-opacity z-10"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(g); setDeleteConfirmName(""); }}
                  data-testid={`group-delete-${g.id}`}
                  title="Elimina gruppo definitivamente"
                  className="absolute top-3 right-12 p-1.5 rounded-md bg-white/80 dark:bg-slate-800/80 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 opacity-70 hover:opacity-100 transition-opacity z-10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dialog rename */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) { setRenameTarget(null); setRenameName(""); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rinomina gruppo</DialogTitle>
            <DialogDescription>
              Modifica solo il nome del gruppo. ID, cantieri associati, dati e permessi restano invariati.
            </DialogDescription>
          </DialogHeader>
          {renameTarget && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">Nome attuale: <span className="font-mono">{renameTarget.name}</span></div>
              <div>
                <Label htmlFor="rename-input">Nuovo nome</Label>
                <Input id="rename-input" value={renameName} onChange={(e) => setRenameName(e.target.value)} data-testid="group-rename-input" autoFocus />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRenameTarget(null); setRenameName(""); }}>Annulla</Button>
            <Button
              disabled={!renameTarget || !renameName.trim() || renameName.trim() === renameTarget?.name || renaming}
              onClick={async () => {
                if (!renameTarget) return;
                const newName = renameName.trim();
                if (!newName || newName === renameTarget.name) return;
                setRenaming(true);
                try {
                  const { data } = await api.patch(`/groups/${renameTarget.id}`, { name: newName });
                  toast.success("Gruppo rinominato");
                  setGroups(gs => gs.map(x => x.id === renameTarget.id ? { ...x, name: data.name } : x));
                  setRenameTarget(null); setRenameName("");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore rinomina");
                } finally { setRenaming(false); }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              data-testid="group-rename-submit"
            >
              <Pencil className="h-4 w-4 mr-1" />Rinomina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog delete */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmName(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-rose-600">Elimina gruppo definitivamente</DialogTitle>
            <DialogDescription>
              ATTENZIONE: questa operazione eliminerà definitivamente il gruppo e TUTTI i dati collegati (cantieri, computi, righe, materiali, ordini, manodopera, rilievi, SAL, ticket, note, allegati metadata, accessi). L&apos;operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 text-sm">
                Per confermare, digita esattamente il nome del gruppo:
                <div className="font-mono font-bold mt-1">{deleteTarget.name}</div>
              </div>
              <Input value={deleteConfirmName} onChange={(e) => setDeleteConfirmName(e.target.value)} placeholder="Digita il nome del gruppo" data-testid="group-delete-confirm-input" autoFocus />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmName(""); }}>Annulla</Button>
            <Button
              disabled={!deleteTarget || deleteConfirmName !== deleteTarget?.name || deleting}
              onClick={async () => {
                if (!deleteTarget || deleteConfirmName !== deleteTarget.name) return;
                setDeleting(true);
                try {
                  const { data } = await api.delete(`/groups/${deleteTarget.id}`, { data: { confirm_name: deleteConfirmName } });
                  toast.success("Gruppo eliminato definitivamente");
                  setGroups(gs => gs.filter(x => x.id !== deleteTarget.id));
                  setDeleteTarget(null); setDeleteConfirmName("");
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Errore eliminazione");
                } finally { setDeleting(false); }
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
              data-testid="group-delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
